import { useCallback, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Row,
  UncontrolledAlert,
} from 'reactstrap';
import Select from 'react-select';
import {
  getAmenitiesEndpoint,
  getProjectTagsEndpoint,
  UnitPcvForm,
} from '../models/unitPcv';
import useAuth from '../../../hooks/useAuth';
import useAdminEntity from '../../../hooks/useAdminEntity';
import { listToSelectOptionAdapter } from '../../../adapters/listAdapter';
import useManageUnitPcvForm from '../hooks/useManageUnitPcvForm';
import { Option } from '../../../models/formModel';
import LandlordSearchInput from '../../../components/landlordInvestors/LandlordSearchInput';
import { getAllowedStatusOptions } from '../constants/unitPcvStatusTransitions';
import { UnitPCVStatus } from '../enums/UnitPcvEnum';

interface Props {
  unitPcv?: UnitPcvForm;
  handleForm: (form: UnitPcvForm, errorRepository: Function) => void;
  financesConfig?: boolean;
}

const ManageUnitPcvForm = ({
  unitPcv,
  handleForm,
  financesConfig = false,
}: Props) => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [catalogError, setCatalogError] = useState(null);

  const {
    handleSubmit,
    errors,
    control,
    setValue,
    isLoading,
    error,
    isEditing,
    setError,
  } = useManageUnitPcvForm(unitPcv);

  //catalogs
  const getAmenities = useCallback(() => getAmenitiesEndpoint(token), []);
  const getTags = useCallback(() => getProjectTagsEndpoint(token), []);

  const [amenities] = useAdminEntity(getAmenities, setCatalogError);
  const [projectTags] = useAdminEntity(getTags, setCatalogError);

  const amenityOptions = listToSelectOptionAdapter(
    (amenities as Array<any>) || [],
    'id',
    'description',
  );

  const projectTagsOptions = listToSelectOptionAdapter(
    (projectTags as Array<any>) || [],
    'id',
    'name',
  );

  const getDefaultSelectValues = (
    options: Option[],
    selectedValues: string[],
  ) => {
    return options.filter((opt) => selectedValues.includes(opt.value));
  };

  const allowedStatusOptions = isEditing
    ? getAllowedStatusOptions(unitPcv?.status ?? '').filter(
        (opt) => financesConfig || opt.value !== UnitPCVStatus.AVAILABLE,
      )
    : [];

  const internalHandleForm = useCallback(
    (form: UnitPcvForm) => handleForm(form, setError),
    [],
  );

  return (
    <Form role="form" onSubmit={handleSubmit(internalHandleForm)}>
      {isEditing && !financesConfig && (
        <Alert color="warning" fade={false} isOpen>
          <span className="alert-inner--icon">
            <i className="ni ni-bell-55" />
          </span>{' '}
          <span className="alert-inner--text">
            <strong>Configuración financiera pendiente.</strong> Esta unidad no
            puede marcarse como disponible hasta que se configure la información
            financiera.
          </span>
        </Alert>
      )}
      {(error || catalogError) && (
        <UncontrolledAlert color="danger" fade={false}>
          <span className="alert-inner--text">{error || catalogError}</span>
        </UncontrolledAlert>
      )}

      <Row>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="name">
              Nombre de la unidad
            </label>
            <Controller
              name="name"
              control={control}
              defaultValue={unitPcv?.name}
              key={`${unitPcv?.name}-key`}
              render={({ field }) => (
                <Input
                  className={errors.name?.message && 'is-invalid'}
                  {...field}
                  type="text"
                />
              )}
            />
            <h5 className="text-danger">{errors.name?.message}</h5>
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="model_name">
              Nombre del modelo
            </label>
            <Controller
              name="model_name"
              control={control}
              defaultValue={unitPcv?.model_name}
              key={`${unitPcv?.model_name}-key`}
              render={({ field }) => (
                <Input
                  className={errors.model_name?.message && 'is-invalid'}
                  {...field}
                  type="text"
                />
              )}
            />
            <h5 className="text-danger">{errors.model_name?.message}</h5>
          </FormGroup>
        </Col>
      </Row>
      {!isEditing && (
        <Row>
          <LandlordSearchInput
            header="Asignar propietario"
            onLandlordfound={(landlordId) =>
              setValue('landlord_id', landlordId)
            }
            error_form_message={errors.landlord_id?.message}
          />
        </Row>
      )}

      {isEditing && allowedStatusOptions.length > 0 && (
        <Row>
          <Col md="6">
            <FormGroup>
              <label className="form-control-label" htmlFor="status">
                Estado
              </label>
              <Controller
                name="status"
                control={control}
                defaultValue={unitPcv?.status}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="select"
                    className={errors.status?.message ? 'is-invalid' : ''}
                  >
                    <option value="">-- Sin cambio --</option>
                    {allowedStatusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                )}
              />
              <h5 className="text-danger">{errors.status?.message}</h5>
            </FormGroup>
          </Col>
        </Row>
      )}

      <Row>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="base_price">
              Precio de venta
            </label>
            <Controller
              name="base_price"
              control={control}
              defaultValue={unitPcv?.base_price}
              key={`${unitPcv?.base_price}-key`}
              render={({ field }) => (
                <Input
                  className={errors.base_price?.message && 'is-invalid'}
                  {...field}
                  type="number"
                  step="any"
                />
              )}
            />
            <h5 className="text-danger">{errors.base_price?.message}</h5>
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="initial_base_price">
              Precio de compra de unidad
            </label>
            <Controller
              name="initial_base_price"
              control={control}
              defaultValue={unitPcv?.initial_base_price}
              key={`${unitPcv?.initial_base_price}-key`}
              render={({ field }) => (
                <Input
                  className={errors.initial_base_price?.message && 'is-invalid'}
                  {...field}
                  type="number"
                  step="any"
                />
              )}
            />
            <h5 className="text-danger">
              {errors.initial_base_price?.message}
            </h5>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="area">
              Área (m²)
            </label>
            <Controller
              name="area"
              control={control}
              defaultValue={unitPcv?.area}
              key={`${unitPcv?.area}-key`}
              render={({ field }) => (
                <Input
                  className={errors.area?.message && 'is-invalid'}
                  {...field}
                  type="number"
                  step="any"
                />
              )}
            />
            <h5 className="text-danger">{errors.area?.message}</h5>
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="square_rods">
              Varas cuadradas
            </label>
            <Controller
              name="square_rods"
              control={control}
              defaultValue={unitPcv?.square_rods}
              key={`${unitPcv?.square_rods}-key`}
              render={({ field }) => (
                <Input
                  className={errors.square_rods?.message && 'is-invalid'}
                  {...field}
                  type="number"
                  step="any"
                />
              )}
            />
            <h5 className="text-danger">{errors.square_rods?.message}</h5>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="4">
          <FormGroup>
            <label className="form-control-label" htmlFor="bedrooms">
              Habitaciones
            </label>
            <Controller
              name="bedrooms"
              control={control}
              defaultValue={unitPcv?.bedrooms}
              key={`${unitPcv?.bedrooms}-key`}
              render={({ field }) => (
                <Input
                  className={errors.bedrooms?.message && 'is-invalid'}
                  {...field}
                  type="number"
                />
              )}
            />
            <h5 className="text-danger">{errors.bedrooms?.message}</h5>
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <label className="form-control-label" htmlFor="bathrooms">
              Baños
            </label>
            <Controller
              name="bathrooms"
              control={control}
              defaultValue={unitPcv?.bathrooms}
              key={`${unitPcv?.bathrooms}-key`}
              render={({ field }) => (
                <Input
                  className={errors.bathrooms?.message && 'is-invalid'}
                  {...field}
                  type="number"
                />
              )}
            />
            <h5 className="text-danger">{errors.bathrooms?.message}</h5>
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <label className="form-control-label" htmlFor="parking">
              Parqueos
            </label>
            <Controller
              name="parking"
              control={control}
              defaultValue={unitPcv?.parking}
              key={`${unitPcv?.parking}-key`}
              render={({ field }) => (
                <Input
                  className={errors.parking?.message && 'is-invalid'}
                  {...field}
                  type="number"
                />
              )}
            />
            <h5 className="text-danger">{errors.parking?.message}</h5>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="level">
              Nivel
            </label>
            <Controller
              name="level"
              control={control}
              defaultValue={unitPcv?.level}
              key={`${unitPcv?.level}-key`}
              render={({ field }) => (
                <Input
                  className={errors.level?.message && 'is-invalid'}
                  {...field}
                  type="number"
                />
              )}
            />
            <h5 className="text-danger">{errors.level?.message}</h5>
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="distribution">
              Distribución
            </label>
            <Controller
              name="distribution"
              control={control}
              defaultValue={unitPcv?.distribution}
              key={`${unitPcv?.distribution}-key`}
              render={({ field }) => (
                <Input
                  className={errors.distribution?.message && 'is-invalid'}
                  {...field}
                  type="text"
                />
              )}
            />
            <h5 className="text-danger">{errors.distribution?.message}</h5>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col>
          <FormGroup>
            <label className="form-control-label" htmlFor="unit_tags">
              Etiquetas
            </label>
            <Controller
              name="unit_tags"
              control={control}
              render={() => (
                <Select
                  key={`tags-${projectTagsOptions.length}`}
                  placeholder="Selecciona etiquetas"
                  isMulti
                  defaultValue={getDefaultSelectValues(
                    projectTagsOptions,
                    unitPcv?.unit_tags || [],
                  )}
                  onChange={(selected) =>
                    setValue(
                      'unit_tags',
                      selected.map((item: any) => item.value),
                    )
                  }
                  options={projectTagsOptions}
                />
              )}
            />
            <h5 className="text-danger">{errors.unit_tags?.message}</h5>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col>
          <FormGroup>
            <label className="form-control-label" htmlFor="unit_amenities">
              Amenidades
            </label>
            <Controller
              name="unit_amenities"
              control={control}
              render={() => (
                <Select
                  key={`amenities-${amenityOptions.length}`}
                  placeholder="Selecciona amenidades"
                  isMulti
                  defaultValue={getDefaultSelectValues(
                    amenityOptions,
                    unitPcv?.unit_amenities || [],
                  )}
                  onChange={(selected) =>
                    setValue(
                      'unit_amenities',
                      selected.map((item: any) => item.value),
                    )
                  }
                  options={amenityOptions}
                />
              )}
            />
            <h5 className="text-danger">{errors.unit_amenities?.message}</h5>
          </FormGroup>
        </Col>
      </Row>

      <Button color="primary" disabled={isLoading}>
        {isEditing ? 'Actualizar' : 'Crear'}
      </Button>
    </Form>
  );
};

export default ManageUnitPcvForm;
